import {
  createAsyncThunk,
  createSlice,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import { RootState } from "../../../app/store";
import {
  WageReport,
  WageReportDeliveryDetails,
  WageReportDeliverySalaryDate,
  WageReportEvent,
  WageReportFilters,
  getDeliveryById,
  getWageReports,
  saveWageReportDeliverySalaryDateAndSend,
  sendDeliveries,
} from "./wageReportsAPI";
import { Hits, Paginated } from "../../../common/types/Paginated";

export type SliceState = {
  ids: [];
  entities: {};
  cursor: string | null;
  hits?: Hits;
  status: "idle" | "pending" | "succeeded" | "failed";
  error?: string;
};

// Code-point order matches the OpenSearch keyword sort. localeCompare does not.
const compareCodePoints = (a: string, b: string) =>
  a < b ? -1 : a > b ? 1 : 0;

const comparePayslipNumber = (a: string, b: string) => {
  const left = Number(a);
  const right = Number(b);
  if (left < right) return 1;
  if (left > right) return -1;
  return 0;
};

const compareAfterSalaryDate = (a: WageReport, b: WageReport) =>
  comparePayslipNumber(a.payslipNumber, b.payslipNumber) ||
  compareCodePoints(a.id, b.id);

const wageReportAdapter = createEntityAdapter<WageReport>({
  sortComparer: (a, b) => {
    if (!a.salaryDate && !b.salaryDate) {
      return compareAfterSalaryDate(a, b);
    }
    if (!a.salaryDate) return 1;
    if (!b.salaryDate) return -1;
    return (
      compareCodePoints(b.salaryDate, a.salaryDate) ||
      compareAfterSalaryDate(a, b)
    );
  },
});

export const fetchWageReportById = createAsyncThunk<
  WageReportDeliveryDetails,
  string,
  { state: RootState }
>("payslips/fetchPayslipById", async (id: string, { requestId }) => {
  const response = await getDeliveryById(id, {
    requestId,
  });
  return response.data;
});

export const fetchWageReports = createAsyncThunk<
  Paginated<WageReport[]>,
  WageReportFilters,
  { state: RootState }
>(
  "wageReports/fetchWageReports",
  async (filters: WageReportFilters, { requestId }) => {
    const response = await getWageReports(filters, {
      requestId,
    });
    return response;
  },
);

export const sendWageReports = createAsyncThunk<
  any,
  string[],
  { state: RootState }
>("talents/sendWageReports", async (wageReportIds: string[], { requestId }) => {
  const res = await sendDeliveries(wageReportIds, { requestId });
  return res.data;
});

export const saveWageReportDeliverySalaryDate = createAsyncThunk<
  WageReportEvent[],
  WageReportDeliverySalaryDate,
  { state: RootState }
>(
  "wageReports/saveWageReportDeliverySalaryDate",
  async (payload, { requestId }) =>
    await saveWageReportDeliverySalaryDateAndSend(payload, { requestId }),
);

export const wageReportSlice = createSlice({
  name: "wageReports",
  initialState: wageReportAdapter.getInitialState({
    status: "idle",
  }) as SliceState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWageReports.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchWageReports.fulfilled, (state, action) => {
        if (!action.meta.arg.cursor) {
          wageReportAdapter.setAll(state, action.payload.data);
        } else {
          wageReportAdapter.upsertMany(state, action.payload.data);
        }
        state.cursor = action.payload.cursor;
        state.hits = action.payload.hits;
        state.status = "succeeded";
      })
      .addCase(fetchWageReports.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(sendWageReports.pending, (state) => {
        state.status = "pending";
      })
      .addCase(sendWageReports.fulfilled, (state) => {
        state.status = "succeeded";
      })
      .addCase(sendWageReports.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      })
      .addCase(saveWageReportDeliverySalaryDate.pending, (state) => {
        state.status = "pending";
      })
      .addCase(saveWageReportDeliverySalaryDate.fulfilled, (state, action) => {
        if (action.payload.length > 0) {
          action.payload.forEach((payload: any) => {
            wageReportAdapter.updateOne(state, {
              id: payload.id,
              changes: { ...payload.payload },
            });
          });
        }
        state.status = "succeeded";
      })
      .addCase(saveWageReportDeliverySalaryDate.rejected, (state, action) => {
        state.error = action.error.message;
        state.status = "failed";
      });
  },
});

export const { selectAll, selectById, selectIds } =
  wageReportAdapter.getSelectors((state: RootState) => state.wageReports);

export default wageReportSlice.reducer;
